import {
  getObject,
  getOptionalObject,
  getList,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { GridSingleSelectRowResultV1, GridSingleSelectRowResultV1DTO } from "./gridSingleSelectRowResultV1";
import { GridSingleSelectGraphTypeV1 } from "./gridSingleSelectGraphTypeV1";
import { GridSingleSelectRowResultWithMeanV1, GridSingleSelectRowResultWithMeanV1DTO } from "./gridSingleSelectRowResultWithMeanV1";
import { GridMultiSelectRowResultV1, GridMultiSelectRowResultV1DTO } from "./gridMultiSelectRowResultV1";
import { GridMultiSelectGraphTypeV1 } from "./gridMultiSelectGraphTypeV1";
import { GridOpenEndedResultWithRowDetailsV1, GridOpenEndedResultWithRowDetailsV1DTO } from "./gridOpenEndedResultWithRowDetailsV1";
import { OpenEndedGraphTypeV1 } from "./openEndedGraphTypeV1";


export namespace GridResultV1Enums {
  export namespace ColumnQuestionType {
    export interface SingleSelectDTO {
      case: string;
      gridSingleSelectRowResults: GridSingleSelectRowResultV1DTO[];
      singleSelectGraphType: GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1DTO;
    }
    
    export class SingleSelect {
      readonly isColumnQuestionTypeSingleSelect: boolean = true;
    
      readonly gridSingleSelectRowResults: GridSingleSelectRowResultV1[];
      readonly singleSelectGraphType: GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1;
    
    
      constructor(
      gridSingleSelectRowResults: GridSingleSelectRowResultV1[], 
      singleSelectGraphType: GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1
      ) {
        this.gridSingleSelectRowResults = gridSingleSelectRowResults;
        this.singleSelectGraphType = singleSelectGraphType;
      }
    
      static fromDTO(dto: SingleSelectDTO): SingleSelect {
        const gridSingleSelectRowResults = getList<GridSingleSelectRowResultV1DTO, GridSingleSelectRowResultV1>(
          dto,
          "gridSingleSelectRowResults",
          GridSingleSelectRowResultV1.fromDTO,
          InvalidGridResultV1ColumnQuestionTypeSingleSelectGridSingleSelectRowResultsError
        );
        const singleSelectGraphType = GridSingleSelectGraphTypeV1.fromDTO(getObject(dto, "singleSelectGraphType", InvalidGridResultV1ColumnQuestionTypeSingleSelectSingleSelectGraphTypeError));
        return new SingleSelect(
          gridSingleSelectRowResults,
          singleSelectGraphType
        );
      }
    
      toDTO(): SingleSelectDTO {
        const dto: SingleSelectDTO = {
          case: "SINGLE_SELECT",
            gridSingleSelectRowResults: this.gridSingleSelectRowResults.map((e: GridSingleSelectRowResultV1) => { return e.toDTO() }),
            singleSelectGraphType: GridSingleSelectGraphTypeV1.toDTO(this.singleSelectGraphType)
        };
        return dto;
      }
      }
    
    SingleSelect.prototype.toString = function toString(): string {
      return `ColumnQuestionType.SINGLE_SELECT(gridSingleSelectRowResults='${this.gridSingleSelectRowResults}',singleSelectGraphType='${this.singleSelectGraphType}')`;
    };
    
    export interface SingleSelectWithProportionDTO {
      case: string;
      gridSingleSelectRowResultsWithMean: GridSingleSelectRowResultWithMeanV1DTO[];
      singleSelectWithProportionGraphType: GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1DTO;
      naOptionCode?: CodeDTO | null;
    }
    
    export class SingleSelectWithProportion {
      readonly isColumnQuestionTypeSingleSelectWithProportion: boolean = true;
    
      readonly gridSingleSelectRowResultsWithMean: GridSingleSelectRowResultWithMeanV1[];
      readonly singleSelectWithProportionGraphType: GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1;
      readonly naOptionCode: Code | null;
    
    
      constructor(
      gridSingleSelectRowResultsWithMean: GridSingleSelectRowResultWithMeanV1[], 
      singleSelectWithProportionGraphType: GridSingleSelectGraphTypeV1.GridSingleSelectGraphTypeV1, 
      naOptionCode: Code | null = null
      ) {
        this.gridSingleSelectRowResultsWithMean = gridSingleSelectRowResultsWithMean;
        this.singleSelectWithProportionGraphType = singleSelectWithProportionGraphType;
        this.naOptionCode = naOptionCode;
      }
    
      static fromDTO(dto: SingleSelectWithProportionDTO): SingleSelectWithProportion {
        const gridSingleSelectRowResultsWithMean = getList<GridSingleSelectRowResultWithMeanV1DTO, GridSingleSelectRowResultWithMeanV1>(
          dto,
          "gridSingleSelectRowResultsWithMean",
          GridSingleSelectRowResultWithMeanV1.fromDTO,
          InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionGridSingleSelectRowResultsWithMeanError
        );
        const singleSelectWithProportionGraphType = GridSingleSelectGraphTypeV1.fromDTO(getObject(dto, "singleSelectWithProportionGraphType", InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionSingleSelectWithProportionGraphTypeError));
        let naOptionCode = getOptionalObject(dto, "naOptionCode");
        if(naOptionCode !== undefined && naOptionCode !== null) {
          naOptionCode = Code.fromDTO(getObject(dto, "naOptionCode", InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionNaOptionCodeError));
        }
        return new SingleSelectWithProportion(
          gridSingleSelectRowResultsWithMean,
          singleSelectWithProportionGraphType,
          naOptionCode
        );
      }
    
      toDTO(): SingleSelectWithProportionDTO {
        const dto: SingleSelectWithProportionDTO = {
          case: "SINGLE_SELECT_WITH_PROPORTION",
            gridSingleSelectRowResultsWithMean: this.gridSingleSelectRowResultsWithMean.map((e: GridSingleSelectRowResultWithMeanV1) => { return e.toDTO() }),
            singleSelectWithProportionGraphType: GridSingleSelectGraphTypeV1.toDTO(this.singleSelectWithProportionGraphType),
        };
        if (this.naOptionCode !== undefined && this.naOptionCode !== null){
          dto.naOptionCode = this.naOptionCode.toDTO();
        }
        return dto;
      }
      }
    
    SingleSelectWithProportion.prototype.toString = function toString(): string {
      return `ColumnQuestionType.SINGLE_SELECT_WITH_PROPORTION(gridSingleSelectRowResultsWithMean='${this.gridSingleSelectRowResultsWithMean}',singleSelectWithProportionGraphType='${this.singleSelectWithProportionGraphType}',naOptionCode='${this.naOptionCode}')`;
    };
    
    export interface MultiSelectDTO {
      case: string;
      multiSelectResults: GridMultiSelectRowResultV1DTO[];
      multiSelectGraphType: GridMultiSelectGraphTypeV1.GridMultiSelectGraphTypeV1DTO;
    }
    
    export class MultiSelect {
      readonly isColumnQuestionTypeMultiSelect: boolean = true;
    
      readonly multiSelectResults: GridMultiSelectRowResultV1[];
      readonly multiSelectGraphType: GridMultiSelectGraphTypeV1.GridMultiSelectGraphTypeV1;
    
    
      constructor(
      multiSelectResults: GridMultiSelectRowResultV1[], 
      multiSelectGraphType: GridMultiSelectGraphTypeV1.GridMultiSelectGraphTypeV1
      ) {
        this.multiSelectResults = multiSelectResults;
        this.multiSelectGraphType = multiSelectGraphType;
      }
    
      static fromDTO(dto: MultiSelectDTO): MultiSelect {
        const multiSelectResults = getList<GridMultiSelectRowResultV1DTO, GridMultiSelectRowResultV1>(
          dto,
          "multiSelectResults",
          GridMultiSelectRowResultV1.fromDTO,
          InvalidGridResultV1ColumnQuestionTypeMultiSelectMultiSelectResultsError
        );
        const multiSelectGraphType = GridMultiSelectGraphTypeV1.fromDTO(getObject(dto, "multiSelectGraphType", InvalidGridResultV1ColumnQuestionTypeMultiSelectMultiSelectGraphTypeError));
        return new MultiSelect(
          multiSelectResults,
          multiSelectGraphType
        );
      }
    
      toDTO(): MultiSelectDTO {
        const dto: MultiSelectDTO = {
          case: "MULTI_SELECT",
            multiSelectResults: this.multiSelectResults.map((e: GridMultiSelectRowResultV1) => { return e.toDTO() }),
            multiSelectGraphType: GridMultiSelectGraphTypeV1.toDTO(this.multiSelectGraphType)
        };
        return dto;
      }
      }
    
    MultiSelect.prototype.toString = function toString(): string {
      return `ColumnQuestionType.MULTI_SELECT(multiSelectResults='${this.multiSelectResults}',multiSelectGraphType='${this.multiSelectGraphType}')`;
    };
    
    export interface OpenEndedDTO {
      case: string;
      openEndedResults: GridOpenEndedResultWithRowDetailsV1DTO[];
      openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1DTO;
    }
    
    export class OpenEnded {
      readonly isColumnQuestionTypeOpenEnded: boolean = true;
    
      readonly openEndedResults: GridOpenEndedResultWithRowDetailsV1[];
      readonly openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1;
    
    
      constructor(
      openEndedResults: GridOpenEndedResultWithRowDetailsV1[], 
      openEndedGraphType: OpenEndedGraphTypeV1.OpenEndedGraphTypeV1
      ) {
        this.openEndedResults = openEndedResults;
        this.openEndedGraphType = openEndedGraphType;
      }
    
      static fromDTO(dto: OpenEndedDTO): OpenEnded {
        const openEndedResults = getList<GridOpenEndedResultWithRowDetailsV1DTO, GridOpenEndedResultWithRowDetailsV1>(
          dto,
          "openEndedResults",
          GridOpenEndedResultWithRowDetailsV1.fromDTO,
          InvalidGridResultV1ColumnQuestionTypeOpenEndedOpenEndedResultsError
        );
        const openEndedGraphType = OpenEndedGraphTypeV1.fromDTO(getObject(dto, "openEndedGraphType", InvalidGridResultV1ColumnQuestionTypeOpenEndedOpenEndedGraphTypeError));
        return new OpenEnded(
          openEndedResults,
          openEndedGraphType
        );
      }
    
      toDTO(): OpenEndedDTO {
        const dto: OpenEndedDTO = {
          case: "OPEN_ENDED",
            openEndedResults: this.openEndedResults.map((e: GridOpenEndedResultWithRowDetailsV1) => { return e.toDTO() }),
            openEndedGraphType: OpenEndedGraphTypeV1.toDTO(this.openEndedGraphType)
        };
        return dto;
      }
      }
    
    OpenEnded.prototype.toString = function toString(): string {
      return `ColumnQuestionType.OPEN_ENDED(openEndedResults='${this.openEndedResults}',openEndedGraphType='${this.openEndedGraphType}')`;
    };
    
  
    export type ColumnQuestionType = SingleSelect | SingleSelectWithProportion | MultiSelect | OpenEnded;
  
    export type ColumnQuestionTypeDTO = SingleSelectDTO | SingleSelectWithProportionDTO | MultiSelectDTO | OpenEndedDTO;
  
    export const fromDTO = (dto: ColumnQuestionTypeDTO): ColumnQuestionType => {
      switch (dto.case) {
        case "SINGLE_SELECT":
            return SingleSelect.fromDTO(dto as SingleSelectDTO);
        case "SINGLE_SELECT_WITH_PROPORTION":
            return SingleSelectWithProportion.fromDTO(dto as SingleSelectWithProportionDTO);
        case "MULTI_SELECT":
            return MultiSelect.fromDTO(dto as MultiSelectDTO);
        case "OPEN_ENDED":
            return OpenEnded.fromDTO(dto as OpenEndedDTO);
        default:
          throw new InvalidGridResultV1ColumnQuestionTypeError(
              `Case ${dto.case} is not valid case of ColumnQuestionType.`
          );
      }
    };
  }
  
}
export interface GridResultV1DTO {
  columnCode: CodeDTO;
  columnHeader?: SurveyLocalizedTextDTO | null;
  columnQuestionType: GridResultV1Enums.ColumnQuestionType.ColumnQuestionTypeDTO;
}

export class GridResultV1 {
  readonly isGridResultV1GridResultV1: boolean = true;

  readonly columnCode: Code;
  readonly columnHeader: SurveyLocalizedText | null;
  readonly columnQuestionType: GridResultV1Enums.ColumnQuestionType.ColumnQuestionType;


  constructor(
  columnCode: Code, 
  columnHeader: SurveyLocalizedText | null = null, 
  columnQuestionType: GridResultV1Enums.ColumnQuestionType.ColumnQuestionType
  ) {
    this.columnCode = columnCode;
    this.columnHeader = columnHeader;
    this.columnQuestionType = columnQuestionType;
  }

  static fromDTO(dto: GridResultV1DTO): GridResultV1 {
    const columnCode = Code.fromDTO(getObject(dto, "columnCode", InvalidGridResultV1ColumnCodeError));
    let columnHeader = getOptionalObject(dto, "columnHeader");
    if(columnHeader !== undefined && columnHeader !== null) {
      columnHeader = SurveyLocalizedText.fromDTO(getObject(dto, "columnHeader", InvalidGridResultV1ColumnHeaderError));
    }
    const columnQuestionType = GridResultV1Enums.ColumnQuestionType.fromDTO(getObject(dto, "columnQuestionType", InvalidGridResultV1ColumnQuestionTypeError));
    return new GridResultV1(
      columnCode,
      columnHeader,
      columnQuestionType
    );
  }

  toDTO(): GridResultV1DTO {
    const dto: GridResultV1DTO = {
        columnCode: this.columnCode.toDTO(),
        columnQuestionType: this.columnQuestionType.toDTO()
    };
    if (this.columnHeader !== undefined && this.columnHeader !== null){
      dto.columnHeader = this.columnHeader.toDTO();
    }
    return dto;
  }
  }

GridResultV1.prototype.toString = function toString(): string {
  return `GridResultV1(columnCode='${this.columnCode}',columnHeader='${this.columnHeader}',columnQuestionType='${this.columnQuestionType}')`;
};

export class InvalidGridResultV1Error extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1Error";
  }
}
export class InvalidGridResultV1ColumnCodeError extends InvalidGridResultV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnCodeError";
  }
}

export class InvalidGridResultV1ColumnHeaderError extends InvalidGridResultV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnHeaderError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeError extends InvalidGridResultV1Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectError extends InvalidGridResultV1ColumnQuestionTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectGridSingleSelectRowResultsError extends InvalidGridResultV1ColumnQuestionTypeSingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectGridSingleSelectRowResultsError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectSingleSelectGraphTypeError extends InvalidGridResultV1ColumnQuestionTypeSingleSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectSingleSelectGraphTypeError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionError extends InvalidGridResultV1ColumnQuestionTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionGridSingleSelectRowResultsWithMeanError extends InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionGridSingleSelectRowResultsWithMeanError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionSingleSelectWithProportionGraphTypeError extends InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionSingleSelectWithProportionGraphTypeError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionNaOptionCodeError extends InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeSingleSelectWithProportionNaOptionCodeError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeMultiSelectError extends InvalidGridResultV1ColumnQuestionTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeMultiSelectError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeMultiSelectMultiSelectResultsError extends InvalidGridResultV1ColumnQuestionTypeMultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeMultiSelectMultiSelectResultsError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeMultiSelectMultiSelectGraphTypeError extends InvalidGridResultV1ColumnQuestionTypeMultiSelectError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeMultiSelectMultiSelectGraphTypeError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeOpenEndedError extends InvalidGridResultV1ColumnQuestionTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeOpenEndedError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeOpenEndedOpenEndedResultsError extends InvalidGridResultV1ColumnQuestionTypeOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeOpenEndedOpenEndedResultsError";
  }
}

export class InvalidGridResultV1ColumnQuestionTypeOpenEndedOpenEndedGraphTypeError extends InvalidGridResultV1ColumnQuestionTypeOpenEndedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidGridResultV1ColumnQuestionTypeOpenEndedOpenEndedGraphTypeError";
  }
}
